import React from 'react';

import InspectorWrapper from '../InspectorWrapper';
import NextLink from '../NextLink';
import NextImage from '../NextImage';
import determineMediaType from '@src/utils/determineMediaType';
import InlineSVG from '../InlineSVG';
import ScrollToWrapper from '../ScrollToWrapper';
import Caption from '../Caption/Caption';

import isEmpty from '@src/utils/isEmpty';
import isFullUrl from '@src/utils/isFullUrl';

import { renderUnorderedListItems } from '../ContentfulRenderer/listVariants';

import { RenderBodyCopyPropTypes } from './RenderBodyCopy.types';

const RenderBodyCopy: React.FC<RenderBodyCopyPropTypes> = ({
  entryId = '',
  bodyCopy,
  entries,
  assets,
  className = '',
  style,
}) => {
  if (isEmpty({ value: bodyCopy })) return null;

  const isStyledInlineNode = (node: any) => {
    if (!node) return false;

    if (node.nodeType === 'hyperlink') return true;

    if (node.nodeType === 'text' && Array.isArray(node.marks) && node.marks.length > 0) {
      return true;
    }

    return false;
  };

  const normalizeTrailingBoundarySpace = (value: string, nextNode: any) => {
    if (!value) return value;

    const endsWithSingleWhitespace = /\s$/.test(value);
    if (!endsWithSingleWhitespace) return value;

    if (!isStyledInlineNode(nextNode)) return value;

    return value.replace(/\s$/, '\u00A0');
  };

  const renderContent = (contentNodes: any[]): React.ReactNode[] => {
    return contentNodes?.map((node, idx) => {
      if (node.nodeType === 'text') {
        const nextNode = contentNodes?.[idx + 1];
        const normalizedValue = normalizeTrailingBoundarySpace(node.value, nextNode);

        let textContent: React.ReactNode = normalizedValue;

        // Apply text formatting marks
        node.marks?.forEach((mark: any) => {
          switch (mark.type) {
            case 'bold':
              textContent = (
                <strong key={`bold-${idx}`} className='font-medium'>
                  {textContent}
                </strong>
              );
              break;
            case 'italic':
              textContent = (
                <em key={`italic-${idx}`} className='italic'>
                  {textContent}
                </em>
              );
              break;
            case 'underline':
              textContent = (
                <u key={`underline-${idx}`} className='underline!'>
                  {textContent}
                </u>
              );
              break;
            case 'superscript':
              textContent = <sup key={`superscript-${idx}`}>{textContent}</sup>;
              break;
            case 'subscript':
              textContent = <sub key={`subscript-${idx}`}>{textContent}</sub>;
              break;
            default:
              break;
          }
        });

        return textContent;
      }

      if (node.nodeType === 'hyperlink') {
        const isExternal = isFullUrl({ url: node.data?.uri });
        const Tag = isExternal ? 'a' : NextLink;

        return (
          <Tag
            key={`link-${idx}`}
            href={node.data?.uri}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className='text-[1.6rem] font-normal underline lg:text-[1.8rem]'
            style={{
              textShadow: 'rgb(143, 66, 0, 0.2) 1px 0px 0px, rgb(143, 66, 0, 0.2) 0.540302px 0.841471px 0px',
            }}
          >
            {renderContent(node.content)}
          </Tag>
        );
      }

      if (node?.nodeType === 'paragraph') {
        return renderContent(node?.content || []);
      }

      if (node.nodeType === 'embedded-entry-block') {
        const targetId = node.data.target.sys.id;
        const entry = entries?.find((e: any) => e.sys.id === targetId);
        if (!entry) return null;

        // Object map for alignment styles
        const alignmentStyles: any = {
          Left: { marginLeft: '0', marginRight: 'auto' },
          Right: { marginLeft: 'auto', marginRight: '0' },
          Center: { margin: '0 auto' },
        };

        // Get the alignment style from the map, default to an empty object if not found
        const alignmentStyle = alignmentStyles[entry?.alignment] || {};

        const imageElement = (
          <>
            {determineMediaType({ url: entry?.media?.url }) === 'image' ? (
              <NextImage
                style={{ width: `${entry?.width}%`, ...alignmentStyle }}
                objectFit='contain'
                imgClassName={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`}
                src={entry?.media?.url}
                alt={entry?.media?.description}
                aspectRatio={entry?.media?.width / entry?.media?.height}
                width={entry?.media?.width}
              />
            ) : null}

            {determineMediaType({ url: entry?.media?.url }) === 'svg' ? (
              <InlineSVG
                style={{ width: `${entry?.width}%`, ...alignmentStyle }}
                src={entry?.media?.url}
                className={`${entry?.boxShadow === 'Yes' ? 'box-shadow' : ''}`}
                renderAt={['desktop', 'tablet', 'mobile']}
              />
            ) : null}

            {entry?.bodyCopy && <Caption bodyCopy={entry?.bodyCopy} />}
          </>
        );

        if (!isEmpty({ value: entry?.url })) {
          if (!isFullUrl({ url: entry?.url })) {
            if (entry?.url?.startsWith('#')) {
              return (
                <ScrollToWrapper key={`embedded-entry-block-${targetId}`} children={imageElement} href={entry?.url} />
              );
            }

            return (
              <NextLink className='inline-block w-full' key={`embedded-entry-block-${targetId}`} href={`${entry?.url}`}>
                {imageElement}
              </NextLink>
            );
          } else {
            return (
              <a
                key={`embedded-entry-block-${targetId}`}
                className='inline-block w-full'
                href={entry?.url}
                target='_blank'
                rel='noopener noreferrer'
              >
                {imageElement}
              </a>
            );
          }
        }

        return imageElement;
      }

      if (node.nodeType === 'embedded-asset-block') {
        const targetId = node.data.target.sys.id;
        const asset = assets?.find((a: any) => a?.sys?.id === targetId);
        if (!asset) return null;

        const { url, width, description, title } = asset;
        return (
          <NextImage
            key={`embedded-asset-block-${targetId}`}
            src={url}
            width={width}
            alt={description || title}
            style={{ width: '100%', height: 'auto' }}
          />
        );
      }

      return null;
    });
  };

  const renderLists = (node: any, idx: number): React.ReactNode => {
    const isOrderedList = node.nodeType === 'ordered-list';

    const Tag = isOrderedList ? 'ol' : 'ul';
    const listClass = isOrderedList ? 'list-decimal' : 'list-disc';

    const items = node.content.map((listItem: any, listIdx: number) => (
      <li key={`list-item-${listIdx}`} className=''>
        {listItem.content.map((paragraphNode: any) => renderContent([paragraphNode]))}
      </li>
    ));

    return (
      <Tag
        key={`list-${idx}`}
        className={`ml-[2.4rem] text-[1.6rem] lg:text-[1.8rem] [&>*:not(:last-child)]:mb-[0.5rem] ${listClass}`}
      >
        {/* Unordered lists support per-item tick/cross variants via leading tokens; token-less items
            and ordered lists are left as-is. */}
        {isOrderedList ? items : renderUnorderedListItems(node, items)}
      </Tag>
    );
  };

  const renderParagraphs = (bodyCopyNodes: any[]) => {
    return bodyCopyNodes?.map((node, idx) => {
      if (node?.nodeType === 'paragraph') {
        // inside renderParagraphs, before the if-skip check…
        const text = node?.content
          .map((c: any) => {
            if (c.nodeType === 'text') {
              return c?.value;
            }
            if (c?.nodeType === 'hyperlink') {
              // grab the link’s label
              return c?.content?.map((child: any) => child?.value).join('');
            }
            return '';
          })
          .join('')
          .trim();

        // if there’s nothing at all (no text, no link labels), skip
        if (!text) {
          return null;
        }

        return (
          <p key={`paragraph-${idx}`} className='text-[1.6rem] text-pretty lg:text-[1.8rem]' style={style}>
            {renderContent(node.content)}
          </p>
        );
      }

      if (node.nodeType === 'unordered-list' || node.nodeType === 'ordered-list') {
        return renderLists(node, idx);
      }

      return renderContent([node]); // Fallback for other types
    });
  };

  return (
    <InspectorWrapper entryId={entryId} fieldId='bodyCopy'>
      <div className={`[&>*:not(:last-child)]:mb-[1.6rem] ${className} `}>{renderParagraphs(bodyCopy)}</div>
    </InspectorWrapper>
  );
};

export default RenderBodyCopy;
